/**
 * Pure (no React/DOM) replay logic for CanvasReplay — extracted so it can be
 * unit-tested. Given a Fabric `canvasJSON` and an optional `strokeTimeline`,
 * produces the ordered list of reveal steps the player animates through.
 *
 * Timing source precedence:
 *   1. `strokeTimeline` — the full event log (preferred).
 *   2. `timestamp` fields embedded on path objects.
 *   3. Synthetic 0.5 s intervals (legacy canvasJSON with no timing at all).
 */

// ── Types ──────────────────────────────────────────────────────────────

export interface FabricPathCommand {
  0: string; // M, Q, L, C, etc.
  [key: number]: number | string;
}

export interface FabricPathObject {
  type: string;
  path: FabricPathCommand[];
  left: number;
  top: number;
  width: number;
  height: number;
  stroke: string;
  strokeWidth: number;
  fill: string | null;
  scaleX?: number;
  scaleY?: number;
  strokeLineCap?: string;
  strokeLineJoin?: string;
  opacity?: number;
  // Custom timestamp fields (from our annotation)
  timestamp?: number;
  strokeIndex?: number;
  isEraser?: boolean;
}

interface FabricCanvasJSON {
  version?: string;
  objects: FabricPathObject[];
}

export interface StrokeTimelineEvent {
  type: 'stroke' | 'erase' | 'undo' | 'redo' | 'clear';
  timestamp: number;
  elapsed: number;
  index: number;
  bbox?: { left: number; top: number; width: number; height: number };
}

export interface ReplayStep {
  pathIndex: number;
  elapsed: number;
  type: 'stroke' | 'erase';
  bbox?: { left: number; top: number; width: number; height: number };
}

export type TimingSource = 'timeline' | 'path-timestamps' | 'synthetic';

// ── Pure helpers ─────────────────────────────────────────────────────────

/** Convert a Fabric path command array to an SVG `d` attribute string. */
export function fabricPathToSvgD(pathCommands: FabricPathCommand[]): string {
  return pathCommands.map(cmd => {
    // Each cmd is a tuple-like array: ["M", x, y] or ["Q", cx, cy, x, y] etc.
    return (cmd as unknown as (string | number)[]).join(' ');
  }).join(' ');
}

/** Parse the canvas JSON, returning only path objects sorted by strokeIndex/order. */
export function parsePaths(canvasJSON: string): FabricPathObject[] {
  try {
    const parsed: FabricCanvasJSON = JSON.parse(canvasJSON);
    return (parsed.objects || [])
      .filter(obj => obj.type.toLowerCase() === 'path' && obj.path)
      .sort((a, b) => (a.strokeIndex ?? 0) - (b.strokeIndex ?? 0));
  } catch {
    return [];
  }
}

/** Compute the bounding box of all paths to set the SVG viewBox. */
export function computeViewBox(paths: FabricPathObject[]): { x: number; y: number; w: number; h: number } {
  if (paths.length === 0) return { x: 0, y: 0, w: 800, h: 600 };

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of paths) {
    const sx = p.scaleX ?? 1;
    const sy = p.scaleY ?? 1;
    const l = p.left;
    const t = p.top;
    const r = l + p.width * sx;
    const b = t + p.height * sy;
    if (l < minX) minX = l;
    if (t < minY) minY = t;
    if (r > maxX) maxX = r;
    if (b > maxY) maxY = b;
  }

  const pad = 20;
  return {
    x: Math.max(0, minX - pad),
    y: Math.max(0, minY - pad),
    w: maxX - minX + pad * 2,
    h: maxY - minY + pad * 2,
  };
}

/**
 * Build the ordered replay steps from the best available timing source.
 * Each step reveals one path at `elapsed` seconds. `strokeTimeline` wins; then
 * per-path `timestamp`; then synthetic 0.5 s intervals.
 */
export function buildReplaySteps(
  strokeTimeline: StrokeTimelineEvent[] | null | undefined,
  allPaths: FabricPathObject[],
): { steps: ReplayStep[]; timingSource: TimingSource } {
  // Source 1: strokeTimeline array (only stroke/erase events reveal a path;
  // undo/redo/clear are bookkeeping and don't add a reveal step).
  if (strokeTimeline && strokeTimeline.length > 0) {
    const strokeEvents = strokeTimeline.filter(e => e.type === 'stroke' || e.type === 'erase');
    return {
      steps: strokeEvents.map((evt, i) => ({
        pathIndex: i,
        elapsed: evt.elapsed,
        type: evt.type as 'stroke' | 'erase',
        bbox: evt.bbox,
      })),
      timingSource: 'timeline',
    };
  }

  // Source 2: timestamp fields on path objects
  const hasPathTimestamps = allPaths.length > 0 && allPaths.some(p => p.timestamp != null);
  if (hasPathTimestamps) {
    const baseTime = allPaths.reduce(
      (min, p) => p.timestamp != null && p.timestamp < min ? p.timestamp : min,
      Infinity,
    );
    return {
      steps: allPaths.map((p, i) => ({
        pathIndex: i,
        elapsed: p.timestamp != null ? (p.timestamp - baseTime) / 1000 : i * 0.5,
        type: (p.isEraser ? 'erase' : 'stroke') as 'stroke' | 'erase',
        bbox: undefined,
      })),
      timingSource: 'path-timestamps',
    };
  }

  // Source 3: synthetic
  return {
    steps: allPaths.map((_, i) => ({
      pathIndex: i,
      elapsed: i * 0.5,
      type: 'stroke' as const,
      bbox: undefined,
    })),
    timingSource: 'synthetic',
  };
}
